import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import InputText from 'LuxKit/Typography/InputText'
import { rem } from 'polished'
import { useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import HiddenInput from './HiddenInput'
import InputLabelWrap from './InputLabelWrap'
import InputWrap from './InputWrap'
import type TextInput from './TextInput'

const FakeInputElement = styled(InputWrap)`
  width: 100%;
  padding: ${rem(12)};
  position: relative;

  &.icon-left {
    padding-inline-start: ${rem(48)};
  }

  &.icon-right {
    padding-inline-end: ${rem(48)};
  }
`

const FakeButton = styled(Clickable)`
  position:absolute;
  inset: 0;
  width: 100%;
`

interface Props extends Omit<React.ComponentProps<typeof TextInput>, 'textInputRef' | 'onClick'> {
  onClick?: React.ComponentProps<typeof Clickable>['onClick']
  /**
   * Class name to be applied to the input wrap
   * Use this to target the wrap to apply styles (such as removing borders for side by side components?)
   */
  inputWrapClassName?: string
}

const FakeInput = React.forwardRef<HTMLLabelElement | null, Props>((props, ref) => {
  const {
    value,
    placeholder,
    disabled,
    label,
    optionalIndicatorEnabled,
    startIcon,
    endIcon,
    onClick,
    className,
    inputWrapClassName,
    ...otherProps
  } = props

  const [error, setError] = useState<string>()
  const showPlaceholder = !value

  return <InputLabelWrap
    label={label}
    required={otherProps.required}
    optionalIndicatorEnabled={optionalIndicatorEnabled}
    ref={ref}
    className={className}
  >
    <FakeInputElement
      {...otherProps}
      className={clsx(inputWrapClassName, { 'icon-left': !!startIcon, 'icon-right': !!endIcon })}
      startIcon={startIcon}
      endIcon={endIcon}
      disabled={disabled}
      error={error}
    >
      {onClick && <FakeButton onClick={onClick} disabled={disabled} data-testid="fake-input-button" />}
      <HiddenInput
        {...otherProps}
        data-testid={undefined}
        value={value}
        disabled={disabled}
        invalidMessage={otherProps.requiredErrorMessage}
        onErrorUpdate={setError}
      />
      <InputText
        variant="text-regular"
        wrap="truncate"
        colour={showPlaceholder ? 'light-foreground-alt' : undefined}
      >
        {value || placeholder}&nbsp;
      </InputText>
    </FakeInputElement>
  </InputLabelWrap>
})

FakeInput.displayName = 'FakeInput'

export default FakeInput
